import {
    acceptCategory,
    eraseCookies,
    getCookie,
    getUserPreferences,
    hide,
    hidePreferences,
    run,
    show,
    showPreferences,
    validConsent,
} from 'vanilla-cookieconsent';
import { applyConsentBrand, shouldLoadAnalyticsTags, shouldLoadHubSpot, shouldShowCookieConsent } from './cookie-consent-host';
import { readAcceptedCategories } from './cookie-consent-state';

export { analyticsGranted, marketingGranted } from './cookie-consent-state';

const GTM_ID = 'GTM-PX7XSDZ';
const COOKIE_NAME = 'dash_cc_cookie';
const OPTIONAL_CATEGORIES = ['necessary', 'analytics', 'marketing'];
const NECESSARY_ONLY = ['necessary'];

const CONSENT_DENIED = {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
};

let consentBarActionsBound = false;
let applyGeneration = 0;
let appliedKey = '';
let pinnedCategories = null;
let pinnedUntil = 0;
let cloudflareClearTimers = [];

function ensureGtagStub() {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
        window.gtag = function gtag() {
            window.dataLayer.push(arguments);
        };
    }
}

function updateConsent(categories) {
    ensureGtagStub();
    const allowAnalytics = categories.indexOf('analytics') !== -1;
    const allowMarketing = categories.indexOf('marketing') !== -1;
    window.gtag('consent', 'update', {
        analytics_storage: allowAnalytics ? 'granted' : 'denied',
        ad_storage: allowMarketing ? 'granted' : 'denied',
        ad_user_data: allowMarketing ? 'granted' : 'denied',
        ad_personalization: allowMarketing ? 'granted' : 'denied',
    });
}

function removeScriptsMatching(srcPart) {
    document.querySelectorAll('script[src*="' + srcPart + '"]').forEach(function (node) {
        node.remove();
    });
}

function resetTagManagerRuntime() {
    removeScriptsMatching('googletagmanager.com/gtm.js');
    removeScriptsMatching('googletagmanager.com/ns.html');
    document.querySelectorAll('iframe[src*="googletagmanager.com"]').forEach(function (node) {
        node.remove();
    });
    try {
        delete window.google_tag_manager;
        delete window.google_tag_data;
    } catch (err) {
        window.google_tag_manager = undefined;
        window.google_tag_data = undefined;
    }
}

function fireGrantedPageView(categories, generation) {
    if (generation !== applyGeneration) {
        return;
    }
    updateConsent(categories);
    window.dataLayer = window.dataLayer || [];
    if (document.readyState === 'complete') {
        window.dataLayer.push({ event: 'gtm.dom' });
        window.dataLayer.push({ event: 'gtm.load' });
    }
    window.dataLayer.push({
        event: 'page_view',
        page_path: window.location.pathname + window.location.search,
        page_location: window.location.href
    });
}

function loadGoogleTagManager(categories, generation) {
    if (!shouldLoadAnalyticsTags()) {
        return;
    }
    // A second Accept must execute gtm.js again. The previous container stays
    // in memory after Reject and ignores a script tag with the same URL.
    resetTagManagerRuntime();
    ensureGtagStub();
    updateConsent(categories);
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtm.js?id=' + GTM_ID + '&t=' + Date.now();
    script.onload = function () {
        fireGrantedPageView(categories, generation);
    };
    document.head.appendChild(script);
}

function disableGoogleTagManager() {
    resetTagManagerRuntime();
    ensureGtagStub();
    window.gtag('consent', 'update', Object.assign({}, CONSENT_DENIED));
}

function cookieEraseDomains() {
    const host = window.location.hostname.toLowerCase();
    const base = host.replace(/^www\./, '');
    const domains = {};
    [host, '.' + host, base, '.' + base].forEach(function (domain) {
        domains[domain] = true;
    });
    const parts = base.split('.');
    if (parts.length > 2) {
        const parent = parts.slice(-2).join('.');
        domains[parent] = true;
        domains['.' + parent] = true;
    }
    return Object.keys(domains);
}

function expireCookieName(name) {
    const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
    const lines = [
        name + '=; expires=' + expires + '; Path=/',
        name + '=; expires=' + expires + '; Path=/; Max-Age=0',
        name + '=; expires=' + expires + '; Path=/; Secure',
        name + '=; expires=' + expires + '; Path=/; Max-Age=0; Secure; SameSite=None',
    ];
    cookieEraseDomains().forEach(function (domain) {
        lines.push(name + '=; expires=' + expires + '; Path=/; Domain=' + domain);
        lines.push(name + '=; expires=' + expires + '; Path=/; Domain=' + domain + '; Max-Age=0');
        lines.push(name + '=; expires=' + expires + '; Path=/; Domain=' + domain + '; Secure');
        lines.push(name + '=; expires=' + expires + '; Path=/; Domain=' + domain + '; Max-Age=0; Secure; SameSite=None');
    });
    cookieEraseDomains().forEach(function (domain) {
        lines.push(name + '=; expires=' + expires + '; Path=/; Domain=' + domain + '; Secure; SameSite=None; Partitioned');
        lines.push(name + '=; expires=' + expires + '; Path=/; Domain=' + domain + '; Max-Age=0; Secure; SameSite=None; Partitioned');
    });
    lines.push(name + '=; expires=' + expires + '; Path=/; Secure; SameSite=None; Partitioned');
    lines.forEach(function (line) {
        document.cookie = line;
    });
    if (window.cookieStore && typeof window.cookieStore.getAll === 'function') {
        window.cookieStore.getAll(name).then(function (cookies) {
            (cookies || []).forEach(function (cookie) {
                window.cookieStore.delete(cookie);
            });
        }).catch(function () {
            /* cookie store can reject partitioned deletes */
        });
    }
}

function eraseTrackingCookies(patterns) {
    const names = document.cookie.split(';').map(function (part) {
        return (part.split('=')[0] || '').trim();
    }).filter(Boolean);
    names.forEach(function (name) {
        const match = patterns.some(function (pattern) {
            return typeof pattern === 'string' ? name === pattern : pattern.test(name);
        });
        if (match) {
            expireCookieName(name);
        }
    });
    patterns.forEach(function (pattern) {
        eraseCookies(pattern);
        eraseCookies(pattern, '/');
        cookieEraseDomains().forEach(function (domain) {
            eraseCookies(pattern, '/', domain);
        });
    });
}

function clearAnalyticsStorage() {
    eraseTrackingCookies([/^_ga/, /^_gat/, '_gid']);
}

function clearMarketingCookies() {
    eraseTrackingCookies(['hubspotutk', '__hstc', '__hssc', '__hssrc', 'messagesUtk']);
}

function clearCloudflareCookies() {
    ['__cf_bm', '_cfuvid', 'cf_clearance'].forEach(function (name) {
        expireCookieName(name);
        eraseCookies(name);
        eraseCookies(name, '/');
        cookieEraseDomains().forEach(function (domain) {
            eraseCookies(name, '/', domain);
        });
    });
}

function cancelCloudflareClear() {
    cloudflareClearTimers.forEach(function (timer) {
        window.clearTimeout(timer);
    });
    cloudflareClearTimers = [];
}

function requestServerCookieClear() {
    // __cf_bm is HttpOnly. A same-origin response has to expire it; document.cookie cannot.
    var urls = ['/consent-clear-bot-cookies', '/api/consent/clear-bot-cookies'];
    function hit(index) {
        if (index >= urls.length) {
            clearCloudflareCookies();
            return;
        }
        fetch(urls[index], {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store',
        }).then(function (response) {
            if (response && response.ok) {
                clearCloudflareCookies();
                return;
            }
            hit(index + 1);
        }).catch(function () {
            hit(index + 1);
        });
    }
    try {
        hit(0);
    } catch (err) {
        /* fetch unavailable */
    }
}

function scheduleCloudflareClear() {
    cancelCloudflareClear();
    clearCloudflareCookies();
    requestServerCookieClear();
    [0, 150, 500, 1200, 2500].forEach(function (delay) {
        cloudflareClearTimers.push(window.setTimeout(function () {
            if (appliedKey && appliedKey !== 'necessary') {
                return;
            }
            clearCloudflareCookies();
        }, delay));
    });
}

function disableHubSpot() {
    const loader = document.getElementById('hs-script-loader');
    if (loader) {
        loader.remove();
    }
    removeScriptsMatching('hs-scripts.com');
    removeScriptsMatching('hs-analytics.net');
    removeScriptsMatching('hs-banner.com');
    if (window.HubSpotConversations && window.HubSpotConversations.widget) {
        try {
            window.HubSpotConversations.widget.remove();
        } catch (err) {
            /* widget not ready */
        }
    }
    try {
        delete window.HubSpotConversations;
        delete window.hubspot;
        delete window.hbspt;
    } catch (err) {
        window.HubSpotConversations = undefined;
        window.hubspot = undefined;
        window.hbspt = undefined;
    }
    if (typeof window.grantHubSpotTracking === 'function') {
        window._hsp = window._hsp || [];
        window._hsp.push(['setHubSpotConsent', {
            analytics: false,
            advertisement: false,
            functionality: false,
        }]);
    }
    window._hsq = [];
    clearMarketingCookies();
}

function applyAnalyticsForCategories(categories, generation) {
    if (categories.indexOf('analytics') !== -1 && shouldLoadAnalyticsTags()) {
        loadGoogleTagManager(categories, generation);
    } else {
        disableGoogleTagManager();
        clearAnalyticsStorage();
    }
}

function applyMarketingForCategories(categories) {
    if (categories.indexOf('marketing') !== -1 && shouldLoadHubSpot()) {
        if (typeof window.grantHubSpotTracking === 'function') {
            window.grantHubSpotTracking();
        }
        if (typeof window.InitializeHubspotChat === 'function') {
            window.InitializeHubspotChat();
        }
        if (typeof window.x === 'function') {
            window.x();
        }
    } else {
        disableHubSpot();
    }
}

function categoriesFromPlugin() {
    if (pinnedCategories && Date.now() < pinnedUntil) {
        return pinnedCategories.slice();
    }
    try {
        const prefs = getUserPreferences();
        if (prefs && Array.isArray(prefs.acceptedCategories) && prefs.acceptedCategories.length) {
            return prefs.acceptedCategories.slice();
        }
    } catch (err) {
        /* plugin not ready */
    }
    return readAcceptedCategories();
}

function categoryKey(categories) {
    return categories.slice().sort().join('|');
}

function applyAllConsentCategories(categories) {
    const accepted = (categories || categoriesFromPlugin()).slice();
    const key = categoryKey(accepted);
    const allowOptional = accepted.indexOf('analytics') !== -1 || accepted.indexOf('marketing') !== -1;
    // Same click also arrives via onConsent, onChange, and a delayed rewrite.
    // Starting Tag Manager again bumps the generation and drops the onload that writes _ga.
    if (key === appliedKey) {
        updateConsent(accepted);
        if (allowOptional) {
            cancelCloudflareClear();
            if (accepted.indexOf('analytics') !== -1 && shouldLoadAnalyticsTags()
                && !document.querySelector('script[src*="googletagmanager.com/gtm.js"]')) {
                loadGoogleTagManager(accepted, applyGeneration);
            }
            if (accepted.indexOf('marketing') !== -1) {
                applyMarketingForCategories(accepted);
            }
        } else {
            scheduleCloudflareClear();
        }
        return;
    }
    appliedKey = key;
    const generation = ++applyGeneration;
    if (allowOptional) {
        cancelCloudflareClear();
    }
    updateConsent(accepted);
    applyAnalyticsForCategories(accepted, generation);
    applyMarketingForCategories(accepted);
    if (!allowOptional) {
        scheduleCloudflareClear();
    }
}

function newConsentId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
    });
}

function forceWriteConsentCookie(acceptAll) {
    const expected = acceptAll ? OPTIONAL_CATEGORIES.slice() : NECESSARY_ONLY.slice();
    let existing = {};
    try {
        existing = getCookie() || {};
    } catch (err) {
        existing = {};
    }
    const now = new Date();
    const expirationTime = now.getTime() + 182 * 24 * 60 * 60 * 1000;
    const value = {
        categories: expected,
        revision: existing.revision || 0,
        data: existing.data || null,
        consentTimestamp: existing.consentTimestamp || now.toISOString(),
        consentId: existing.consentId || newConsentId(),
        services: {
            necessary: [],
            analytics: acceptAll ? ['gtm'] : [],
            marketing: acceptAll ? ['hubspot'] : [],
        },
        languageCode: existing.languageCode || 'en',
        lastConsentTimestamp: now.toISOString(),
        expirationTime: expirationTime,
    };
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const encoded = COOKIE_NAME + '=' + encodeURIComponent(JSON.stringify(value))
        + '; expires=' + new Date(expirationTime).toUTCString()
        + '; Path=/; SameSite=Lax' + secure;
    document.cookie = encoded;
    // The plugin also stores a Domain-scoped cookie on real hosts. Write both
    // so a second Accept/Reject replaces the cookie the plugin reads.
    if (window.location.hostname.indexOf('.') !== -1) {
        document.cookie = encoded + '; Domain=' + window.location.hostname;
    }
}

function consentButtonFromEvent(target) {
    let el = target;
    if (el && el.nodeType !== 1) {
        el = el.parentElement;
    }
    while (el && el !== document) {
        if (el.tagName === 'BUTTON') {
            const role = el.getAttribute('data-role');
            if (role === 'all' || role === 'necessary') {
                return el;
            }
        }
        el = el.parentElement;
    }
    return null;
}

function persistConsentChoice(acceptAll) {
    const expected = acceptAll ? OPTIONAL_CATEGORIES.slice() : NECESSARY_ONLY.slice();
    pinnedCategories = expected.slice();
    pinnedUntil = Date.now() + 8000;
    // HubSpot's loader reads dash_cc_cookie before it injects its script.
    forceWriteConsentCookie(acceptAll);
    applyAllConsentCategories(expected);
    try {
        acceptCategory(acceptAll ? 'all' : []);
    } catch (err) {
        /* plugin can throw on a second choice; tags were already started above */
    }
    try {
        hide();
        hidePreferences();
    } catch (err) {
        /* banner already hidden */
    }
    forceWriteConsentCookie(acceptAll);
    // The plugin applies its own button one frame later. Run again after that
    // so Tag Manager and HubSpot still start from this click.
    window.requestAnimationFrame(function () {
        window.setTimeout(function () {
            forceWriteConsentCookie(acceptAll);
            applyAllConsentCategories(expected);
        }, 0);
    });
}

function bindConsentBarActions() {
    if (consentBarActionsBound || typeof document === 'undefined') {
        return;
    }
    consentBarActionsBound = true;
    document.addEventListener('click', function (event) {
        const button = consentButtonFromEvent(event.target);
        if (!button) {
            return;
        }
        event.preventDefault();
        persistConsentChoice(button.getAttribute('data-role') === 'all');
    }, true);
}

export function openCookiePreferences() {
    if (!shouldShowCookieConsent()) {
        return;
    }
    showPreferences();
}

export function runCookieConsent() {
    if (typeof document === 'undefined' || !shouldShowCookieConsent()) {
        return Promise.resolve();
    }

    applyConsentBrand();
    ensureGtagStub();
    window.gtag('consent', 'default', Object.assign({ wait_for_update: 500 }, CONSENT_DENIED));
    window.openCookiePreferences = openCookiePreferences;
    bindConsentBarActions();

    return run({
        mode: 'opt-in',
        autoShow: true,
        autoClearCookies: true,
        manageScriptTags: false,
        hideFromBots: false,
        respectGpc: true,
        cookie: {
            name: COOKIE_NAME,
            secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
        },
        guiOptions: {
            consentModal: {
                layout: 'bar inline',
                position: 'bottom',
                equalWeightButtons: true,
                flipButtons: false,
            },
            preferencesModal: {
                layout: 'bar wide',
            },
        },
        categories: {
            necessary: {
                enabled: true,
                readOnly: true,
            },
            analytics: {
                autoClear: {
                    cookies: [{ name: /^_ga/ }, { name: '_gid' }, { name: /^_gat/ }],
                },
                services: {
                    gtm: {
                        label: 'Google Tag Manager (' + GTM_ID + ')',
                    },
                },
            },
            marketing: {
                autoClear: {
                    cookies: [
                        { name: 'hubspotutk' },
                        { name: '__hstc' },
                        { name: '__hssc' },
                        { name: '__hssrc' },
                        { name: 'messagesUtk' },
                        { name: '__cf_bm' },
                        { name: '_cfuvid' },
                        { name: 'cf_clearance' },
                    ],
                },
                services: {
                    hubspot: {
                        label: 'Marketing cookies',
                    },
                },
            },
        },
        onConsent: function () {
            applyAllConsentCategories(categoriesFromPlugin());
        },
        onChange: function () {
            applyAllConsentCategories(categoriesFromPlugin());
        },
        language: {
            default: 'en',
            translations: {
                en: {
                    consentModal: {
                        label: 'Cookie notice',
                        description: 'We use cookies and similar technologies to operate this reporting dashboard and remember your cookie choice. Google Tag Manager runs only if you accept. Strictly necessary cookies stay on so you can sign in.',
                        acceptAllBtn: 'Accept all',
                        acceptNecessaryBtn: 'Reject all',
                    },
                    preferencesModal: {
                        title: 'Cookie preferences',
                        acceptAllBtn: 'Accept all',
                        acceptNecessaryBtn: 'Reject all',
                        savePreferencesBtn: 'Save preferences',
                        closeIconLabel: 'Close',
                        sections: [
                            {
                                title: 'Cookie usage',
                                description: 'Accept all or reject optional cookies. Strictly necessary cookies always stay on.',
                            },
                            {
                                title: 'Strictly necessary',
                                description: 'Required to sign in and remember this cookie choice (dash_cc_cookie).',
                                linkedCategory: 'necessary',
                            },
                            {
                                title: 'Analytics',
                                description: 'Google Tag Manager (' + GTM_ID + ') runs only when you accept.',
                                linkedCategory: 'analytics',
                            },
                            {
                                title: 'Marketing',
                                description: 'Marketing cookies run only when you accept.',
                                linkedCategory: 'marketing',
                            },
                        ],
                    },
                },
            },
        },
    }).then(function () {
        if (validConsent()) {
            applyAllConsentCategories(readAcceptedCategories());
        } else {
            show(true);
        }
    });
}
